import type { ButtonTooltipConfig } from '@/shared/types/buttonConfig';
import type { TooltipAlignment, TooltipPosition } from '@/shared/types/types';

/** Fuente plana o agrupada de tooltip (compatible con migración gradual). */
export interface ButtonTooltipSource {
  /** Texto simple o configuración agrupada (`label`, `position`, …). */
  tooltip?: string | ButtonTooltipConfig;
  /** @deprecated Usar `tooltip.position`. */
  tooltipPosition?: TooltipPosition;
  /** @deprecated Usar `tooltip.alignment`. */
  tooltipAlignment?: TooltipAlignment;
  /** @deprecated Usar `tooltip.delay`. */
  tooltipDelay?: number;
}

export interface ResolvedButtonTooltipProps {
  tooltip?: string | ButtonTooltipConfig;
  tooltipProps?: ButtonTooltipConfig;
}

/**
 * Fusiona tooltip plano y agrupado hacia la API de `Button` / `InnerButton`.
 * Las props de raíz deprecadas se combinan con `tooltip` objeto (el objeto prevalece).
 */
export function resolveButtonTooltipProps(
  source: ButtonTooltipSource | undefined
): ResolvedButtonTooltipProps {
  if (!source) return {};

  const { tooltip, tooltipPosition, tooltipAlignment, tooltipDelay } = source;

  if (
    tooltip === undefined &&
    tooltipPosition === undefined &&
    tooltipAlignment === undefined &&
    tooltipDelay === undefined
  ) {
    return {};
  }

  if (tooltip && typeof tooltip === 'object') {
    return { tooltip };
  }

  const label = typeof tooltip === 'string' ? tooltip : undefined;
  const hasExtra =
    tooltipPosition !== undefined ||
    tooltipAlignment !== undefined ||
    tooltipDelay !== undefined;

  if (label && !hasExtra) {
    return { tooltip: label };
  }

  const config: ButtonTooltipConfig = {
    ...(label ? { label } : {}),
    ...(tooltipPosition !== undefined ? { position: tooltipPosition } : {}),
    ...(tooltipAlignment !== undefined ? { alignment: tooltipAlignment } : {}),
    ...(tooltipDelay !== undefined ? { delay: tooltipDelay } : {}),
  };

  if (!config.label) {
    return {};
  }

  return hasExtra || typeof config.label !== 'string' ? { tooltip: config } : { tooltip: config.label };
}

/** Etiqueta efectiva del tooltip resuelto (para `pointer={false}` en botones disabled). */
export function getResolvedTooltipLabel(props: ResolvedButtonTooltipProps): string | undefined {
  if (typeof props.tooltip === 'string') {
    return props.tooltip;
  }
  return props.tooltip?.label ?? props.tooltipProps?.label;
}
